import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, finalize } from 'rxjs';
import { FeatureMapUserAccount } from '../../../admin/interfaces/user-accout.interface';
import { ModalService } from '../../../services/modal.service';
import { NotificationService } from '../../../services/notification.service';
import { DefaultPermissionsPatch, UserAccountService } from '../../../services/user-account.service';

export const DEFAULT_PERMISSIONS_MODAL_ID = 'default-permissions-modal';

// Matches the backend ErrorTokens.IncompatibleDefaultPermissions value.
const incompatibleDefaultPermissionsToken = 'INCOMPATIBLE_DEFAULT_PERMISSIONS';

@Component({
    standalone: false,
    selector: 'por-default-permissions-modal',
    templateUrl: './default-permissions-modal.component.html',
    styleUrls: ['./default-permissions-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DefaultPermissionsModalComponent implements OnInit, OnDestroy {
    private readonly fb = inject(FormBuilder);
    private readonly userAccountService = inject(UserAccountService);
    private readonly modalService = inject(ModalService);
    private readonly notificationService = inject(NotificationService);
    private readonly translateService = inject(TranslateService);
    private readonly cdr = inject(ChangeDetectorRef);

    @Input() customerId!: string;

    readonly modalId = DEFAULT_PERMISSIONS_MODAL_ID;

    featureNames: string[] = [];
    permissionsForm!: FormGroup;
    isLoading = false;
    isSaving = false;
    loadSucceeded = false;
    hasConflict = false;

    private readonly subscriptions: Subscription[] = [];

    private initialValues: Record<string, boolean> = {};

    get hasChanges(): boolean {
        if (!this.permissionsForm) {
            return false;
        }
        const current = this.permissionsForm.getRawValue() as Record<string, boolean>;
        return this.featureNames.some(name => current[name] !== this.initialValues[name]);
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.userAccountService.getVisibleFeatures().subscribe(features => {
                this.featureNames = features.map(f => f.featureName);
                const controls: Record<string, FormControl<boolean>> = {};
                for (const name of this.featureNames) {
                    controls[name] = new FormControl<boolean>(false, { nonNullable: true });
                }
                this.permissionsForm = this.fb.group(controls);
                this.cdr.markForCheck();
                this.load();
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    load(): void {
        if (!this.customerId) {
            return;
        }
        this.loadSucceeded = false;
        this.isLoading = true;
        this.cdr.markForCheck();

        this.subscriptions.push(
            this.userAccountService
                .getDefaultPermissions(this.customerId)
                .pipe(
                    finalize(() => {
                        this.isLoading = false;
                        this.cdr.markForCheck();
                    })
                )
                .subscribe({
                    next: (response: FeatureMapUserAccount) => {
                        const features = response.featureUserAccounts ?? {};
                        const patch: Record<string, boolean> = {};
                        for (const name of this.featureNames) {
                            patch[name] = (features as Record<string, boolean>)[name] ?? false;
                        }
                        this.initialValues = { ...patch };
                        this.permissionsForm.patchValue(patch);
                        this.loadSucceeded = true;
                        this.cdr.markForCheck();
                    },
                    error: () => {
                        this.notificationService.error(this.translateService.instant('DefaultPermissions.LoadError'));
                    }
                })
        );
    }

    onSave(): void {
        if (!this.customerId || !this.loadSucceeded || !this.hasChanges || this.isSaving || this.hasConflict) {
            return;
        }

        const patch: DefaultPermissionsPatch = {};
        const value = this.permissionsForm.getRawValue() as Record<string, boolean>;
        for (const name of this.featureNames) {
            if (value[name] !== this.initialValues[name]) {
                patch[name] = value[name];
            }
        }

        this.isSaving = true;
        this.cdr.markForCheck();

        this.subscriptions.push(
            this.userAccountService
                .setDefaultPermissions(this.customerId, patch)
                .pipe(
                    finalize(() => {
                        this.isSaving = false;
                        this.cdr.markForCheck();
                    })
                )
                .subscribe({
                    next: () => {
                        this.notificationService.success(this.translateService.instant('DefaultPermissions.SaveSuccess'));
                        this.modalService.close(this.modalId);
                    },
                    error: (err: unknown) => {
                        const token = (err as { error?: { token?: string } })?.error?.token;
                        if (token === incompatibleDefaultPermissionsToken) {
                            // Server rejected a state the UI didn't catch (e.g. concurrent edits in another tab).
                            this.hasConflict = true;
                            this.notificationService.error(this.translateService.instant('DefaultPermissions.OperatorConflict'));
                            this.cdr.markForCheck();
                            return;
                        }
                        this.notificationService.error(this.translateService.instant('DefaultPermissions.SaveError'));
                    }
                })
        );
    }

    onCancel(): void {
        this.modalService.close(this.modalId);
    }
}
